import React, { useState, useRef, useEffect } from 'react';
import { Calculator, Keyboard, X, Move } from 'lucide-react';
import { supportsVirtualKeyboard } from '../../utils/deviceDetection';

interface UnifiedMathInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
}

/**
 * 统一数学输入组件
 * 集成文本输入、符号助手和虚拟键盘于一体
 */
export const UnifiedMathInput: React.FC<UnifiedMathInputProps> = ({
  value,
  onChange,
  onKeyPress,
  placeholder = "输入文本或数学公式...",
  className = "",
  rows = 2,
  disabled = false,
  readOnly = false,
  autoFocus = false
}) => {
  const [showSymbolHelper, setShowSymbolHelper] = useState(false);
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false);
  const [mathFieldInstance, setMathFieldInstance] = useState<any>(null);
  const [mathLiveLoaded, setMathLiveLoaded] = useState(false);
  const [mathLiveLoading, setMathLiveLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mathFieldRef = useRef<HTMLDivElement>(null);
  const [deviceSupportsVirtualKeyboard, setDeviceSupportsVirtualKeyboard] = useState(false);

  // 拖动相关状态
  const [symbolHelperPosition, setSymbolHelperPosition] = useState({ x: 0, y: 0 });
  const [virtualKeyboardPosition, setVirtualKeyboardPosition] = useState({ x: 0, y: 0 });
  const [isDraggingSymbol, setIsDraggingSymbol] = useState(false);
  const [isDraggingKeyboard, setIsDraggingKeyboard] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const symbolHelperRef = useRef<HTMLDivElement>(null);
  const virtualKeyboardRef = useRef<HTMLDivElement>(null);

  // 检测设备能力和预加载 MathLive
  useEffect(() => {
    setDeviceSupportsVirtualKeyboard(supportsVirtualKeyboard());

    // 预检查 MathLive 是否已加载
    if (typeof window !== 'undefined' && (window as any).MathfieldElement) {
      console.log('UnifiedMathInput: MathLive already available');
      setMathLiveLoaded(true);
    }
  }, []);

  // 拖动处理函数
  const handleMouseDown = (e: React.MouseEvent, type: 'symbol' | 'keyboard') => {
    e.preventDefault();
    const rect = (type === 'symbol' ? symbolHelperRef.current : virtualKeyboardRef.current)?.getBoundingClientRect();
    if (!rect) return;

    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    if (type === 'symbol') {
      setIsDraggingSymbol(true);
    } else {
      setIsDraggingKeyboard(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDraggingSymbol) {
      setSymbolHelperPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    } else if (isDraggingKeyboard) {
      setVirtualKeyboardPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDraggingSymbol(false);
    setIsDraggingKeyboard(false);
  };

  // 添加全局鼠标事件监听
  useEffect(() => {
    if (isDraggingSymbol || isDraggingKeyboard) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingSymbol, isDraggingKeyboard, dragOffset]);

  // 常用数学符号
  const mathSymbols = [
    { symbol: '\\frac{a}{b}', display: 'a/b', desc: '分数' },
    { symbol: 'x^{2}', display: 'x²', desc: '上标' },
    { symbol: 'x_{1}', display: 'x₁', desc: '下标' },
    { symbol: '\\sqrt{x}', display: '√x', desc: '平方根' },
    { symbol: '\\sqrt[n]{x}', display: 'ⁿ√x', desc: 'n次根' },
    { symbol: '\\sum_{i=1}^{n}', display: '∑', desc: '求和' },
    { symbol: '\\int_{a}^{b}', display: '∫', desc: '积分' },
    { symbol: '\\lim_{x \\to 0}', display: 'lim', desc: '极限' },
    { symbol: '\\alpha', display: 'α', desc: 'alpha' },
    { symbol: '\\beta', display: 'β', desc: 'beta' },
    { symbol: '\\gamma', display: 'γ', desc: 'gamma' },
    { symbol: '\\delta', display: 'δ', desc: 'delta' },
    { symbol: '\\pi', display: 'π', desc: 'pi' },
    { symbol: '\\theta', display: 'θ', desc: 'theta' },
    { symbol: '\\lambda', display: 'λ', desc: 'lambda' },
    { symbol: '\\mu', display: 'μ', desc: 'mu' },
    { symbol: '\\leq', display: '≤', desc: '小于等于' },
    { symbol: '\\geq', display: '≥', desc: '大于等于' },
    { symbol: '\\neq', display: '≠', desc: '不等于' },
    { symbol: '\\approx', display: '≈', desc: '约等于' },
    { symbol: '\\pm', display: '±', desc: '正负' },
    { symbol: '\\times', display: '×', desc: '乘号' },
    { symbol: '\\div', display: '÷', desc: '除号' },
    { symbol: '\\cdot', display: '·', desc: '点乘' },
    { symbol: '\\infty', display: '∞', desc: '无穷' }
  ];

  // 加载 MathLive - 多CDN降级方案
  const loadMathLive = async () => {
    if (mathLiveLoaded || mathLiveLoading) return;

    setMathLiveLoading(true);
    console.log('UnifiedMathInput: Loading MathLive...');

    try {
      // 检查是否已经加载了 MathLive
      if (typeof window !== 'undefined' && (window as any).MathfieldElement) {
        console.log('UnifiedMathInput: MathLive already loaded');
        setMathLiveLoaded(true);
        setMathLiveLoading(false);
        return;
      }

      // 多个CDN源，按优先级尝试
      const cdnSources = [
        'https://unpkg.com/mathlive@0.98.6/dist/mathlive.min.js',
        'https://cdn.jsdelivr.net/npm/mathlive@0.98.6/dist/mathlive.min.js',
        'https://cdnjs.cloudflare.com/ajax/libs/mathlive/0.98.6/mathlive.min.js'
      ];

      let loadSuccess = false;

      for (const src of cdnSources) {
        if (loadSuccess) break;

        try {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => {
              console.log(`UnifiedMathInput: MathLive loaded from ${src}`);
              loadSuccess = true;
              resolve(true);
            };
            script.onerror = () => {
              console.warn(`UnifiedMathInput: Failed to load from ${src}`);
              document.head.removeChild(script);
              reject(new Error(`Failed to load from ${src}`));
            };
            document.head.appendChild(script);
          });
        } catch (error) {
          console.warn(`UnifiedMathInput: CDN ${src} failed:`, error);
          continue;
        }
      }

      if (loadSuccess) {
        setMathLiveLoaded(true);
      } else {
        console.error('UnifiedMathInput: All CDN sources failed');
      }
    } catch (error) {
      console.error('UnifiedMathInput: Error loading MathLive:', error);
    } finally {
      setMathLiveLoading(false);
    }
  };

  // 虚拟键盘初始化 - 带降级方案
  useEffect(() => {
    if (showVirtualKeyboard && !mathFieldInstance) {
      const initMathField = async () => {
        try {
          console.log('UnifiedMathInput: Starting MathField initialization...');

          // 确保 MathLive 已加载
          if (!mathLiveLoaded) {
            console.log('UnifiedMathInput: MathLive not loaded, loading now...');
            await loadMathLive();

            // 等待加载完成，最多等待3秒
            let attempts = 0;
            while (attempts < 30 && !(window as any).MathfieldElement) {
              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }
          }

          if (!mathFieldRef.current) {
            console.error('UnifiedMathInput: MathField container not available');
            setMathFieldInstance('error');
            return;
          }

          if (!(window as any).MathfieldElement) {
            console.error('UnifiedMathInput: MathLive not available after loading attempts');
            setMathFieldInstance('error');
            return;
          }

          const MathfieldElement = (window as any).MathfieldElement;
          console.log('UnifiedMathInput: Creating MathField...');

          // 创建 mathfield
          const mf = new MathfieldElement();

          // 设置样式
          mf.style.width = '100%';
          mf.style.minHeight = '60px';
          mf.style.border = '1px solid #ddd';
          mf.style.borderRadius = '8px';
          mf.style.padding = '8px';
          mf.style.fontSize = '16px';

          // 设置事件监听
          mf.addEventListener('input', () => {
            try {
              const latex = mf.getValue('latex');
              if (latex && latex.trim()) {
                console.log('UnifiedMathInput: MathField input:', latex);
                insertAtCursor(latex);
                // 清空 mathfield 以便下次输入
                setTimeout(() => {
                  try {
                    mf.setValue('');
                  } catch (e) {
                    console.warn('UnifiedMathInput: Error clearing mathfield:', e);
                  }
                }, 100);
              }
            } catch (error) {
              console.error('UnifiedMathInput: Error handling mathfield input:', error);
            }
          });

          // 清空容器并添加 mathfield
          mathFieldRef.current.innerHTML = '';
          mathFieldRef.current.appendChild(mf);
          setMathFieldInstance(mf);

          // 聚焦到 mathfield
          setTimeout(() => {
            try {
              mf.focus();
              console.log('UnifiedMathInput: MathField initialized successfully');
            } catch (error) {
              console.warn('UnifiedMathInput: Error focusing mathfield:', error);
            }
          }, 200);

        } catch (error) {
          console.error('UnifiedMathInput: Error creating MathField:', error);
          setMathFieldInstance('error');

          // 显示降级方案
          if (mathFieldRef.current) {
            mathFieldRef.current.innerHTML = `
              <div style="padding: 20px; text-align: center; color: #666; border: 1px dashed #ccc; border-radius: 8px;">
                <p>虚拟键盘暂时不可用</p>
                <p style="font-size: 12px; margin-top: 8px;">请使用符号助手或直接输入 LaTeX 语法</p>
              </div>
            `;
          }
        }
      };

      initMathField();
    }

    // 清理函数
    return () => {
      if (mathFieldInstance && showVirtualKeyboard === false) {
        console.log('UnifiedMathInput: Cleaning up MathField...');
        if (mathFieldInstance !== 'error' && typeof mathFieldInstance.blur === 'function') {
          try {
            mathFieldInstance.blur();
          } catch (error) {
            console.warn('UnifiedMathInput: Error during cleanup:', error);
          }
        }
        setMathFieldInstance(null);
      }
    };
  }, [showVirtualKeyboard, mathLiveLoaded]);

  // 插入符号到光标位置
  const insertAtCursor = (text: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.substring(0, start) + text + value.substring(end);

    onChange(newValue);

    // 恢复光标位置
    setTimeout(() => {
      const newCursorPos = start + text.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const handleSymbolClick = (symbol: string) => {
    insertAtCursor(symbol);
  };

  const handleToggleVirtualKeyboard = async () => {
    console.log('UnifiedMathInput: Toggle virtual keyboard, current state:', showVirtualKeyboard);

    if (showVirtualKeyboard) {
      setShowVirtualKeyboard(false);
      if (mathFieldInstance && mathFieldInstance !== 'error') {
        try {
          if (typeof mathFieldInstance.blur === 'function') {
            mathFieldInstance.blur();
          }
        } catch (error) {
          console.warn('UnifiedMathInput: Error hiding virtual keyboard:', error);
        }
        setMathFieldInstance(null);
      }
    } else {
      // 设置初始位置（屏幕中央偏上）
      if (virtualKeyboardPosition.x === 0 && virtualKeyboardPosition.y === 0) {
        setVirtualKeyboardPosition({
          x: Math.max(50, (window.innerWidth - 400) / 2),
          y: Math.max(100, window.innerHeight * 0.2)
        });
      }

      // 预加载 MathLive
      if (!mathLiveLoaded && !mathLiveLoading) {
        await loadMathLive();
      }
      setShowVirtualKeyboard(true);
    }
  };

  const handleToggleSymbolHelper = () => {
    if (showSymbolHelper) {
      setShowSymbolHelper(false);
    } else {
      // 设置初始位置（屏幕右侧）
      if (symbolHelperPosition.x === 0 && symbolHelperPosition.y === 0) {
        setSymbolHelperPosition({
          x: Math.max(50, window.innerWidth - 370),
          y: Math.max(100, window.innerHeight * 0.3)
        });
      }
      setShowSymbolHelper(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* 主输入区域 */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          className={`w-full px-4 py-3 pr-20 pb-12 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${readOnly ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : ''
            }`}
          rows={rows}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
        />

        {/* 右上角：虚拟键盘按钮 */}
        {!disabled && !readOnly && deviceSupportsVirtualKeyboard && (
          <button
            type="button"
            onClick={handleToggleVirtualKeyboard}
            disabled={mathLiveLoading}
            className={`absolute top-2 right-2 p-2 rounded-lg transition-colors z-20 ${showVirtualKeyboard
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : mathLiveLoading
                ? 'bg-yellow-200 text-yellow-600 cursor-wait'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
              }`}
            title={
              mathLiveLoading
                ? '加载中...'
                : showVirtualKeyboard
                  ? '关闭虚拟键盘'
                  : '打开虚拟键盘'
            }
          >
            <Keyboard className="w-4 h-4" />
          </button>
        )}

        {/* 右下角：符号助手按钮 */}
        {!disabled && !readOnly && (
          <button
            type="button"
            onClick={handleToggleSymbolHelper}
            className={`absolute bottom-2 right-2 p-2 rounded-lg transition-colors z-20 ${showSymbolHelper
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
              }`}
            title={showSymbolHelper ? '关闭符号助手' : '打开符号助手'}
          >
            <Calculator className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 符号助手面板 - 可拖动 */}
      {showSymbolHelper && (
        <div
          ref={symbolHelperRef}
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-4 z-50 max-h-60 overflow-y-auto min-w-[320px]"
          style={{
            left: symbolHelperPosition.x || 'auto',
            top: symbolHelperPosition.y || 'auto',
            cursor: isDraggingSymbol ? 'grabbing' : 'default'
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <button
                onMouseDown={(e) => handleMouseDown(e, 'symbol')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing"
                title="拖动面板"
              >
                <Move className="w-4 h-4" />
              </button>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">数学符号助手</h3>
            </div>
            <button
              onClick={() => setShowSymbolHelper(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {mathSymbols.map((item, index) => (
              <button
                key={index}
                onClick={() => handleSymbolClick(item.symbol)}
                className="p-2 text-center border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={`${item.desc}: ${item.symbol}`}
              >
                <div className="text-lg">{item.display}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 虚拟键盘容器 - 可拖动 */}
      {showVirtualKeyboard && (
        <div
          ref={virtualKeyboardRef}
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 min-w-[400px]"
          style={{
            left: virtualKeyboardPosition.x || 'auto',
            top: virtualKeyboardPosition.y || 'auto',
            cursor: isDraggingKeyboard ? 'grabbing' : 'default'
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <button
                onMouseDown={(e) => handleMouseDown(e, 'keyboard')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing"
                title="拖动面板"
              >
                <Move className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">虚拟键盘</span>
            </div>
            <button
              onClick={() => setShowVirtualKeyboard(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {mathLiveLoading && (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p>加载虚拟键盘中...</p>
              <p className="text-xs mt-1">正在从 CDN 下载 MathLive 库</p>
            </div>
          )}

          {!mathLiveLoading && !mathFieldInstance && (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-pulse w-6 h-6 bg-gray-300 rounded-full mx-auto mb-2"></div>
              <p>正在初始化虚拟键盘...</p>
              <p className="text-xs mt-1">请稍候片刻</p>
            </div>
          )}

          {mathFieldInstance === 'error' && (
            <div className="text-center py-8 text-red-500">
              <X className="w-6 h-6 mx-auto mb-2" />
              <p>虚拟键盘暂时不可用</p>
              <p className="text-xs mt-2 text-gray-500">
                网络问题或浏览器兼容性问题<br />
                请使用符号助手或直接输入 LaTeX 语法
              </p>
              <button
                onClick={() => {
                  setMathFieldInstance(null);
                  setMathLiveLoaded(false);
                  setMathLiveLoading(false);
                }}
                className="mt-3 px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                重试加载
              </button>
            </div>
          )}

          <div ref={mathFieldRef} className="min-h-[80px]" />

          {mathFieldInstance && mathFieldInstance !== 'error' && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              💡 在上方输入框中输入数学公式，内容会自动插入到文本框中
            </div>
          )}
        </div>
      )}

      {/* 状态提示 */}
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
        <span>
          支持LaTeX 数学公式
          {deviceSupportsVirtualKeyboard && (
            <span className="ml-2 text-blue-500">• 右上角虚拟键盘 • 右下角符号助手</span>
          )}
          {!deviceSupportsVirtualKeyboard && (
            <span className="ml-2 text-green-500">• 右下角符号助手</span>
          )}
        </span>
      </div>
    </div>
  );
};

export default UnifiedMathInput;